import { useCallback, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import { BulkExcelUploadTab, FeeManagementListTab } from './components';
import { SCUILoadingLinear } from '../../atomic/atoms';
import { CargoManagementQuery } from '../../services/query';
import { MPCFM } from './mpCargoFeeManagement.classes';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
    className?: string;
}

const TabPanel = ({ children, value, index, className, ...other }: TabPanelProps): React.JSX.Element | null => {
    if (value !== index) return null;
    return (
        <Paper
            component="div"
            className={className}
            role="tabpanel"
            id={`mp-cargo-fee-tabpanel-${index}`}
            aria-labelledby={`mp-cargo-fee-tab-${index}`}
            sx={{
                p: '24px',
                display: 'flex',
                flex: 1,
                backgroundColor: 'grey.50',
            }}
            {...other}
        >
            {children}
        </Paper>
    );
};

interface TabConfig {
    label: string;
    Component: React.ComponentType<any>;
    tabClassName: string;
    tabPanelClassName: string;
}

interface CargoCompanyOption {
    id: number;
    name: string;
}

export const MPCargoFeeManagement = (): React.JSX.Element => {
    const [tabValue, setTabValue] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [companyList, setCompanyList] = useState<CargoCompanyOption[]>([]);

    const { t } = useTranslation();

    const handleChange = (_: any, newValue: number): void => {
        setTabValue(newValue);
    };

    const tabsConfig: TabConfig[] = [
        {
            label: t('mpCargoFeeManagement.tabs.list'),
            Component: FeeManagementListTab as React.ComponentType<any>,
            tabClassName: MPCFM.tabList,
            tabPanelClassName: MPCFM.tabPanelList,
        },
        {
            label: t('mpCargoFeeManagement.tabs.bulkUpload'),
            Component: BulkExcelUploadTab as React.ComponentType<any>,
            tabClassName: MPCFM.tabBulkUpload,
            tabPanelClassName: MPCFM.tabPanelBulkUpload,
        },
    ];

    const getCompanies = useCallback(() => {
        setIsLoading(true);
        CargoManagementQuery.CargoCompanyList()
            .then((res: any) => {
                const data = res?.data || [];
                const mapped: CargoCompanyOption[] = data?.map((d: any) => ({
                    id: d.id || d.shippingCompanyId,
                    name: d.name || d.shippingCompanyName,
                }));
                setCompanyList(mapped);
            })
            .catch(() => setCompanyList([]))
            .finally(() => setIsLoading(false));
    }, [t]);

    useEffect(() => {
        getCompanies();
    }, []);

    return (
        <SCUIContent>
            <Box className={MPCFM.page}>
                <Paper
                    className={MPCFM.shellPaper}
                    component="div"
                    sx={{
                        p: '24px',
                        paddingTop: '36px',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <div className={MPCFM.breadcrumbSection}>
                        <SCUIBreadCrumb
                            title={t('mpCargoFeeManagement.title')}
                            componentLink={PageUrl.mpCargoFeeManagement.url}
                        />
                    </div>
                    <div className={MPCFM.loadingSection}>
                        <SCUILoadingLinear display={isLoading} />
                    </div>
                    <Box className={MPCFM.tabsWrap}>
                        <Tabs
                            className={MPCFM.tabsRoot}
                            sx={{ marginTop: '12px' }}
                            value={tabValue}
                            onChange={handleChange}
                            aria-label="MP Cargo Fee Management Tabs"
                        >
                            {tabsConfig.map((tab, index) => (
                                <Tab
                                    key={index}
                                    className={tab.tabClassName}
                                    sx={{ fontSize: '14px' }}
                                    label={tab.label}
                                    id={`mp-cargo-fee-tab-${index}`}
                                    aria-controls={`mp-cargo-fee-tabpanel-${index}`}
                                />
                            ))}
                        </Tabs>
                    </Box>
                    {tabsConfig.map((tab, index) => (
                        <TabPanel key={index} value={tabValue} index={index} className={tab.tabPanelClassName}>
                            <tab.Component setIsLoading={setIsLoading} isLoading={isLoading} companyOptions={companyList} />
                        </TabPanel>
                    ))}
                </Paper>
            </Box>
        </SCUIContent>
    );
};
